import React, { useMemo } from 'react';
import {
  CompetencyStatus,
  MatrixMember,
  MatrixSection,
  MatrixSkill,
  SkillCategory,
  SKILL_CATEGORIES,
  cellKey,
  isCellApplicable,
} from '../../config';
import StatusCell from '../StatusCell';

interface MatrixTableProps {
  members: MatrixMember[];
  sections: MatrixSection[];
  getStatus: (memberId: string, skillId: number) => CompetencyStatus;
  pendingCells: Set<string>;
  canEdit: boolean;
  onCycle: (memberId: string, skillId: number) => void;
  onEditSection: (section: MatrixSection) => void;
  onEditSkill: (skill: MatrixSkill, section: MatrixSection) => void;
  onAddSkill: (section: MatrixSection) => void;
}

const MatrixTable: React.FC<MatrixTableProps> = ({
  members,
  sections,
  getStatus,
  pendingCells,
  canEdit,
  onCycle,
  onEditSection,
  onEditSkill,
  onAddSkill,
}) => {
  const sectionsByCategory = useMemo(() => {
    const grouped = new Map<SkillCategory, MatrixSection[]>();
    SKILL_CATEGORIES.forEach(category => {
      grouped.set(category, sections.filter(s => s.category === category));
    });
    return grouped;
  }, [sections]);

  // Skill name + standards + member columns + count column.
  const totalColumns = members.length + 3;

  const achievedCount = (skill: MatrixSkill, category: SkillCategory): number =>
    members.filter(m =>
      isCellApplicable(m, category) && getStatus(m.id, skill.id) === 'Achieved'
    ).length;

  return (
    <div className="skills-matrix__scroll">
      <table className="skills-matrix__table">
        <thead>
          <tr>
            <th className="skills-matrix__corner">Skill / Competency</th>
            <th className="skills-matrix__standards-head">Key Standard(s)</th>
            {members.map(member => (
              <th key={member.id} className="skills-matrix__member-head">
                <span className="skills-matrix__member-name">
                  {member.first_name} {member.last_name}
                </span>
                <span className="skills-matrix__member-role">{member.type}</span>
              </th>
            ))}
            <th className="skills-matrix__count-head"># Achieved</th>
          </tr>
        </thead>
        <tbody>
          {SKILL_CATEGORIES.map(category => {
            const categorySections = sectionsByCategory.get(category) || [];
            if (categorySections.length === 0) return null;
            return (
              <React.Fragment key={category}>
                <tr className="skills-matrix__category-row">
                  <td colSpan={totalColumns}>
                    <span className="skills-matrix__band-label">
                      {category}
                      {category === 'Contract Administration' && (
                        <span className="skills-matrix__category-note">
                          Engineers only — Draftsperson columns not applicable
                        </span>
                      )}
                    </span>
                  </td>
                </tr>
                {categorySections.map(section => (
                  <React.Fragment key={section.id}>
                    <tr className="skills-matrix__section-row">
                      <td colSpan={totalColumns}>
                        <span className="skills-matrix__band-label">
                          <span className="skills-matrix__section-name">{section.name}</span>
                          {canEdit && (
                            <span className="skills-matrix__section-actions">
                              <button
                                type="button"
                                className="skills-matrix__link-btn"
                                onClick={() => onAddSkill(section)}
                              >
                                + Skill
                              </button>
                              <button
                                type="button"
                                className="skills-matrix__link-btn"
                                onClick={() => onEditSection(section)}
                              >
                                Edit
                              </button>
                            </span>
                          )}
                        </span>
                      </td>
                    </tr>
                    {section.skills.length === 0 ? (
                      <tr className="skills-matrix__empty-row">
                        <td colSpan={totalColumns}>
                          <span className="skills-matrix__band-label">
                            No skills in this section yet{canEdit ? ' — use “+ Skill” to add one.' : '.'}
                          </span>
                        </td>
                      </tr>
                    ) : (
                      section.skills.map(skill => (
                        <tr key={skill.id} className="skills-matrix__skill-row">
                          <td className="skills-matrix__skill-name">
                            {canEdit ? (
                              <button
                                type="button"
                                className="skills-matrix__skill-edit"
                                onClick={() => onEditSkill(skill, section)}
                                title="Edit skill"
                              >
                                {skill.name}
                              </button>
                            ) : (
                              skill.name
                            )}
                          </td>
                          <td className="skills-matrix__standards">
                            {skill.key_standards || '—'}
                          </td>
                          {members.map(member => (
                            <StatusCell
                              key={member.id}
                              status={getStatus(member.id, skill.id)}
                              applicable={isCellApplicable(member, category)}
                              canEdit={canEdit}
                              pending={pendingCells.has(cellKey(member.id, skill.id))}
                              onCycle={() => onCycle(member.id, skill.id)}
                            />
                          ))}
                          <td className="skills-matrix__count">
                            {achievedCount(skill, category)}
                          </td>
                        </tr>
                      ))
                    )}
                  </React.Fragment>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default MatrixTable;
